/*eslint-disable*/
import {
  useTheme,
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Divider,
  Grid,
  Chip,
  IconButton,
  Paper,
  LinearProgress,
} from "@mui/material";
import {
  Edit,
  Email,
  Person,
  Shield,
  Star,
  Verified,
  Settings,
  Camera,
} from "@mui/icons-material";
import { useEffect } from "react";
import { listUserDetails } from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { tokens } from "../../theme";

const Profile = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(listUserDetails());
  }, [dispatch]);

  const userProfile = useSelector((state) => state.userProfile?.profile);
  const loading = useSelector((state) => state.loading);

  // Calculate profile completion
  const calculateCompletion = () => {
    if (!userProfile) return 0;
    const fields = ["username", "email", "id"];
    const completed = fields.filter((field) => userProfile[field]).length;
    return Math.round((completed / fields.length) * 100);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="70vh"
        sx={{
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[600]} 100%)`,
          borderRadius: "20px",
          margin: "20px",
        }}
      >
        <Avatar
          sx={{
            width: 80,
            height: 80,
            backgroundColor: colors.greenAccent[500],
            mb: 2,
            animation: "pulse 2s infinite",
            "@keyframes pulse": {
              "0%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.1)" },
              "100%": { transform: "scale(1)" },
            },
          }}
        >
          <Person sx={{ fontSize: "2rem" }} />
        </Avatar>
        <Typography variant="h4" color={colors.grey[100]} mb={2}>
          Loading Your Profile...
        </Typography>
        <LinearProgress
          sx={{
            width: "200px",
            borderRadius: "10px",
            backgroundColor: colors.grey[700],
            "& .MuiLinearProgress-bar": {
              backgroundColor: colors.greenAccent[500],
            },
          }}
        />
      </Box>
    );
  }

  if (!userProfile) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="70vh"
        sx={{
          background: `linear-gradient(135deg, ${colors.redAccent[800]} 0%, ${colors.redAccent[600]} 100%)`,
          borderRadius: "20px",
          margin: "20px",
        }}
      >
        <Avatar
          sx={{
            width: 80,
            height: 80,
            backgroundColor: colors.redAccent[500],
            mb: 2,
          }}
        >
          <Person sx={{ fontSize: "2rem" }} />
        </Avatar>
        <Typography variant="h4" color={colors.grey[100]}>
          No Profile Data Available
        </Typography>
      </Box>
    );
  }

  const completion = calculateCompletion();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[700]} 50%, ${colors.primary[900]} 100%)`,
        p: 3,
      }}
    >
      {/* Floating Header */}
      <Paper
        elevation={20}
        sx={{
          background: `linear-gradient(135deg, ${colors.greenAccent[800]} 0%, ${colors.greenAccent[600]} 100%)`,
          borderRadius: "20px",
          p: 3,
          mb: 4,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography
              variant="h2"
              sx={{
                background: `linear-gradient(45deg, ${colors.grey[100]} 30%, #ffffff 90%)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: "bold",
                mb: 1,
              }}
            >
              User Profile
            </Typography>
            <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.8)" }}>
              Manage your personal information and preferences
            </Typography>
          </Box>
          <IconButton
            sx={{
              backgroundColor: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(10px)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.3)",
                transform: "scale(1.1)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <Settings sx={{ color: "white", fontSize: "1.5rem" }} />
          </IconButton>
        </Box>
      </Paper>

      <Grid container spacing={4}>
        {/* Main Profile Card */}
        <Grid item xs={12} lg={8}>
          <Paper
            elevation={15}
            sx={{
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[600]} 100%)`,
              borderRadius: "25px",
              overflow: "hidden",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "120px",
                background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[500]} 100%)`,
              },
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {/* Cover Area */}
              <Box
                sx={{
                  height: "120px",
                  position: "relative",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "flex-end",
                  p: 2,
                }}
              >
                <IconButton
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    backdropFilter: "blur(10px)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.3)",
                    },
                  }}
                >
                  <Edit sx={{ color: "white" }} />
                </IconButton>
              </Box>

              {/* Profile Content */}
              <Box sx={{ p: 4, pt: 0 }}>
                {/* Avatar Section */}
                <Box
                  display="flex"
                  alignItems="flex-end"
                  mb={3}
                  sx={{ mt: "-60px" }}
                >
                  <Box position="relative">
                    <Avatar
                      sx={{
                        width: 120,
                        height: 120,
                        fontSize: "3rem",
                        backgroundColor: colors.greenAccent[500],
                        border: `4px solid ${colors.primary[400]}`,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                        background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.greenAccent[700]} 100%)`,
                      }}
                    >
                      {userProfile.username
                        ? userProfile.username.charAt(0).toUpperCase()
                        : "U"}
                    </Avatar>
                    <IconButton
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        backgroundColor: colors.blueAccent[500],
                        width: 35,
                        height: 35,
                        "&:hover": {
                          backgroundColor: colors.blueAccent[400],
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      <Camera sx={{ fontSize: "1rem", color: "white" }} />
                    </IconButton>
                  </Box>

                  <Box ml={3} flex={1}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography
                        variant="h3"
                        sx={{
                          color: colors.grey[100],
                          fontWeight: "bold",
                          mr: 2,
                        }}
                      >
                        {userProfile.username || "No Username"}
                      </Typography>
                      <Chip
                        icon={<Verified />}
                        label="Verified"
                        size="small"
                        sx={{
                          backgroundColor: colors.greenAccent[500],
                          color: "white",
                          fontWeight: "bold",
                        }}
                      />
                    </Box>
                    <Typography variant="h6" color={colors.grey[300]} mb={2}>
                      {userProfile.email || "No email provided"}
                    </Typography>

                    {/* Profile Stats */}
                    <Box display="flex" gap={3}>
                      <Box textAlign="center">
                        <Typography
                          variant="h4"
                          color={colors.greenAccent[400]}
                          fontWeight="bold"
                        >
                          {completion}%
                        </Typography>
                        <Typography variant="body2" color={colors.grey[400]}>
                          Complete
                        </Typography>
                      </Box>
                      <Box textAlign="center">
                        <Typography
                          variant="h4"
                          color={colors.blueAccent[400]}
                          fontWeight="bold"
                        >
                          Active
                        </Typography>
                        <Typography variant="body2" color={colors.grey[400]}>
                          Status
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Divider
                  sx={{
                    backgroundColor: colors.grey[700],
                    mb: 3,
                    opacity: 0.3,
                  }}
                />

                {/* Profile Information Grid */}
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: "15px",
                        backgroundColor: "rgba(255,255,255,0.05)",
                        backdropFilter: "blur(10px)",
                        border: `1px solid rgba(255,255,255,0.1)`,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.1)",
                          transform: "translateY(-5px)",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={2}>
                        <Person
                          sx={{ color: colors.greenAccent[400], mr: 1 }}
                        />
                        <Typography
                          variant="h6"
                          color={colors.greenAccent[400]}
                          fontWeight="bold"
                        >
                          Username
                        </Typography>
                      </Box>
                      <Typography variant="h6" color={colors.grey[100]}>
                        {userProfile.username || "Not provided"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: "15px",
                        backgroundColor: "rgba(255,255,255,0.05)",
                        backdropFilter: "blur(10px)",
                        border: `1px solid rgba(255,255,255,0.1)`,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.1)",
                          transform: "translateY(-5px)",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={2}>
                        <Email sx={{ color: colors.blueAccent[400], mr: 1 }} />
                        <Typography
                          variant="h6"
                          color={colors.blueAccent[400]}
                          fontWeight="bold"
                        >
                          Email Address
                        </Typography>
                      </Box>
                      <Typography variant="h6" color={colors.grey[100]}>
                        {userProfile.email || "Not provided"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          {/* Profile Completion Card */}
          <Paper
            elevation={10}
            sx={{
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[600]} 100%)`,
              borderRadius: "20px",
              p: 3,
              mb: 3,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Typography
              variant="h5"
              color={colors.grey[100]}
              fontWeight="bold"
              mb={3}
            >
              Profile Completion
            </Typography>

            <Box textAlign="center" mb={3}>
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background: `conic-gradient(${colors.greenAccent[500]} ${
                      completion * 3.6
                    }deg, ${colors.grey[700]} 0deg)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 90,
                      height: 90,
                      borderRadius: "50%",
                      backgroundColor: colors.primary[500],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="h4"
                      color={colors.greenAccent[400]}
                      fontWeight="bold"
                    >
                      {completion}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Typography variant="body1" color={colors.grey[300]}>
                Your profile is {completion}% complete
              </Typography>
            </Box>
          </Paper>

          {/* Quick Actions Card */}
          <Paper
            elevation={10}
            sx={{
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[600]} 100%)`,
              borderRadius: "20px",
              p: 3,
            }}
          >
            <Typography
              variant="h5"
              color={colors.grey[100]}
              fontWeight="bold"
              mb={3}
            >
              Quick Actions
            </Typography>

            <Box display="flex" flexDirection="column" gap={2}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                    transform: "translateX(10px)",
                  },
                }}
              >
                <Edit sx={{ color: colors.blueAccent[400], mr: 2 }} />
                <Typography color={colors.grey[100]}>Edit Profile</Typography>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                    transform: "translateX(10px)",
                  },
                }}
              >
                <Settings sx={{ color: colors.greenAccent[400], mr: 2 }} />
                <Typography color={colors.grey[100]}>
                  Account Settings
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;
